import { useState, useMemo } from "react";
import { Employee, EmployeeEvaluation } from "@/types/employee";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, TrendingUp, Award, Calendar } from "lucide-react";
import { ConversationAISummary } from "@/components/ConversationAISummary";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Label } from "@/components/ui/label";
import { PerformanceThreshold, getDefaultThresholds, getPerformanceLevelFromThresholds } from "@/types/performanceThresholds";

interface DashboardProps {
  employees: Employee[];
  evaluations: EmployeeEvaluation[];
  userRole?: 'admin' | 'vorarbeiter' | 'hr_manager' | 'employee' | null;
  currentEmployeeId?: string | null;
}

const Dashboard = ({ employees, evaluations, userRole, currentEmployeeId }: DashboardProps) => {
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  
  const isEmployee = userRole === 'employee';

  // Load thresholds from localStorage
  const thresholds = useMemo((): PerformanceThreshold[] => {
    const stored = localStorage.getItem('performanceThresholds');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return getDefaultThresholds();
      }
    }
    return getDefaultThresholds();
  }, []);

  // Build dynamic colors map from thresholds
  const COLORS = useMemo(() => {
    const colorMap: Record<string, string> = {};
    thresholds.forEach(t => {
      colorMap[t.level] = `hsl(var(--${t.color}))`;
    });
    return colorMap;
  }, [thresholds]);
  
  // For employees: filter to only show their own data with name, others anonymized
  const getDisplayName = (employeeId: string, employeeName: string) => {
    if (!isEmployee) return employeeName;
    return employeeId === currentEmployeeId ? employeeName : 'Mitarbeiter';
  };

  // Verfügbare Monate extrahieren
  const availableMonths = [...new Set(evaluations.map(e => e.month))].sort().reverse();

  // Gefilterte Evaluations basierend auf ausgewähltem Monat
  const filteredEvaluations = selectedMonth 
    ? evaluations.filter(e => e.month === selectedMonth)
    : evaluations;

  // Statistiken berechnen (mit Filter)
  const totalEmployees = employees.length;
  const totalEvaluations = filteredEvaluations.length;
  const averageScore =
    filteredEvaluations.length > 0
      ? filteredEvaluations.reduce((sum, e) => sum + (e.maxScore > 0 ? (e.totalScore / e.maxScore) * 100 : 0), 0) /
        filteredEvaluations.length
      : 0;

  // Leistungslevel Verteilung (mit Filter) - recalculate using thresholds
  const performanceLevels = filteredEvaluations.reduce((acc, evaluation) => {
    // Recalculate performance level based on current thresholds
    const level = getPerformanceLevelFromThresholds(evaluation.totalScore, evaluation.maxScore, thresholds);
    acc[level] = (acc[level] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const performanceData = Object.entries(performanceLevels).map(([name, value]) => ({
    name,
    value,
  }));

  // Top Performer (mit Filter)
  const employeeScores = employees.map((employee) => {
    const employeeEvals = filteredEvaluations.filter((e) => e.employeeId === employee.id);
    if (employeeEvals.length === 0) return null;

    const avgScore =
      employeeEvals.reduce(
        (sum, e) => sum + (e.maxScore > 0 ? (e.totalScore / e.maxScore) * 100 : 0),
        0
      ) / employeeEvals.length;

    return {
      id: employee.id,
      name: getDisplayName(employee.id, employee.name),
      score: avgScore,
      evaluations: employeeEvals.length,
      isCurrentUser: employee.id === currentEmployeeId,
    };
  }).filter(Boolean) as { id: string; name: string; score: number; evaluations: number; isCurrentUser: boolean }[];

  const topPerformers = [...employeeScores].sort((a, b) => b.score - a.score).slice(0, 5);

  // Kumulative Performance-Berechnung
  const cumulativeData = employees.map(employee => {
    const employeeEvals = evaluations
      .filter(e => e.employeeId === employee.id)
      .sort((a, b) => a.month.localeCompare(b.month));
    
    if (employeeEvals.length === 0) return null;

    let cumulativeScore = 0;
    let cumulativeMax = 0;
    
    const monthlyProgress = employeeEvals.map(evaluation => {
      cumulativeScore += evaluation.totalScore;
      cumulativeMax += evaluation.maxScore;
      
      return {
        month: evaluation.month,
        percentage: (cumulativeScore / cumulativeMax) * 100,
        totalEvaluations: employeeEvals.indexOf(evaluation) + 1
      };
    });
    
    return {
      employeeId: employee.id,
      employeeName: getDisplayName(employee.id, employee.name),
      progress: monthlyProgress,
      currentPercentage: monthlyProgress.length > 0 
        ? monthlyProgress[monthlyProgress.length - 1].percentage 
        : 0,
      isCurrentUser: employee.id === currentEmployeeId,
    };
  }).filter(Boolean) as Array<{
    employeeId: string;
    employeeName: string;
    progress: Array<{ month: string; percentage: number; totalEvaluations: number }>;
    currentPercentage: number;
    isCurrentUser: boolean;
  }>;

  cumulativeData.sort((a, b) => b.currentPercentage - a.currentPercentage);

  // Chart-Daten für kumulative Performance
  const cumulativeChartData = (() => {
    const allMonths = [...new Set(evaluations.map(evaluation => evaluation.month))].sort();
    
    return allMonths.map(month => {
      const dataPoint: any = { month };
      
      cumulativeData.forEach(empData => {
        const relevantProgress = empData.progress.filter(p => p.month <= month);
        if (relevantProgress.length > 0) {
          dataPoint[empData.employeeName] = relevantProgress[relevantProgress.length - 1].percentage;
        }
      });
      
      return dataPoint;
    });
  })();

  const CHART_COLORS = [
    "hsl(var(--chart-1))",
    "hsl(var(--chart-2))",
    "hsl(var(--chart-3))",
    "hsl(var(--chart-4))",
    "hsl(var(--chart-5))",
  ];

  const getVariantForLevel = (level: string): "default" | "secondary" | "destructive" | "outline" => {
    // Find the threshold for this level
    const threshold = thresholds.find(t => t.level === level);
    if (threshold) {
      if (threshold.color === 'success' || threshold.color === 'chart-3') return "default";
      if (threshold.color === 'primary') return "secondary";
      if (threshold.color === 'destructive') return "destructive";
      if (threshold.color === 'warning') return "outline";
    }
    return "outline";
  };

  // Monatliche Trends
  const monthlyData = evaluations.reduce((acc, evaluation) => {
    const month = evaluation.month;
    if (!acc[month]) {
      acc[month] = { month, totalScore: 0, maxScore: 0, count: 0 };
    }
    acc[month].totalScore += evaluation.totalScore;
    acc[month].maxScore += evaluation.maxScore;
    acc[month].count += 1;
    return acc;
  }, {} as Record<string, { month: string; totalScore: number; maxScore: number; count: number }>);

  const trendData = Object.values(monthlyData)
    .map((data) => ({
      month: new Date(data.month + "-01").toLocaleDateString("de-DE", {
        month: "short",
        year: "numeric",
      }),
      durchschnitt: data.maxScore > 0 ? (data.totalScore / data.maxScore) * 100 : 0,
    }))
    .sort((a, b) => a.month.localeCompare(b.month))
    .slice(-6);

  return (
    <div className="space-y-6">
      {/* Monatsfilter */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
            <Label className="text-sm font-medium whitespace-nowrap">Monat filtern:</Label>
            <Select value={selectedMonth || "all"} onValueChange={(val) => setSelectedMonth(val === "all" ? null : val)}>
              <SelectTrigger className="w-full sm:w-[250px]">
                <SelectValue placeholder="Alle Monate" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Monate</SelectItem>
                {availableMonths.map(month => (
                  <SelectItem key={month} value={month}>
                    {new Date(month + "-01").toLocaleDateString("de-DE", { month: "long", year: "numeric" })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Mitarbeiter</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalEmployees}</div>
            <p className="text-xs text-muted-foreground">Aktive Mitarbeiter</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Bewertungen</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalEvaluations}</div>
            <p className="text-xs text-muted-foreground">Gesamt durchgeführt</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Durchschnitt</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{averageScore.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">Durchschnittliche Leistung</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Top Performer</CardTitle>
            <Award className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {topPerformers.length > 0 ? (isEmployee ? (topPerformers[0].isCurrentUser ? topPerformers[0].name.split(" ")[0] : "Mitarbeiter") : topPerformers[0].name.split(" ")[0]) : "-"}
            </div>
            <p className="text-xs text-muted-foreground">
              {topPerformers.length > 0 ? `${topPerformers[0].score.toFixed(1)}%` : "Keine Daten"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Monatsspezifische Mitarbeitertabelle */}
      {selectedMonth && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg">
              Mitarbeiter-Übersicht für {new Date(selectedMonth + "-01").toLocaleDateString("de-DE", { month: "long", year: "numeric" })}
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[120px]">Name</TableHead>
                  <TableHead className="min-w-[120px]">Position</TableHead>
                  <TableHead className="min-w-[100px]">Punktzahl</TableHead>
                  <TableHead className="min-w-[80px]">Prozent</TableHead>
                  <TableHead className="min-w-[120px]">Leistungsniveau</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {employees.map(emp => {
                  const evaluation = filteredEvaluations.find(e => e.employeeId === emp.id);
                  if (!evaluation) return null;
                  
                  const percentage = (evaluation.totalScore / evaluation.maxScore) * 100;
                  const displayName = getDisplayName(emp.id, emp.name);
                  // Recalculate performance level based on current thresholds
                  const performanceLevel = getPerformanceLevelFromThresholds(evaluation.totalScore, evaluation.maxScore, thresholds);
                  
                  return (
                    <TableRow key={emp.id} className={emp.id === currentEmployeeId ? "bg-primary/5" : ""}>
                      <TableCell className="font-medium whitespace-nowrap">{displayName}</TableCell>
                      <TableCell className="whitespace-nowrap">{isEmployee && emp.id !== currentEmployeeId ? "-" : emp.position}</TableCell>
                      <TableCell className="whitespace-nowrap">{evaluation.totalScore.toFixed(1)} / {evaluation.maxScore}</TableCell>
                      <TableCell className="whitespace-nowrap">{percentage.toFixed(1)}%</TableCell>
                      <TableCell>
                        <Badge variant={getVariantForLevel(performanceLevel)} className="whitespace-nowrap">
                          {performanceLevel}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Top 5 Mitarbeiter</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topPerformers}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="score" fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Leistungsniveau Verteilung</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={performanceData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {performanceData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[entry.name as keyof typeof COLORS] || "hsl(var(--muted))"} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {trendData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Monatlicher Leistungstrend</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="durchschnitt"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Kumulative Gesamtbewertung */}
      {cumulativeChartData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Fortlaufende Gesamtbewertung</CardTitle>
            <p className="text-sm text-muted-foreground">
              Kumulative Performance-Entwicklung über alle Monate
            </p>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={cumulativeChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="month" 
                  tickFormatter={(month) => 
                    new Date(month + "-01").toLocaleDateString("de-DE", { month: "short", year: "2-digit" })
                  }
                />
                <YAxis label={{ value: "Kumulative Performance %", angle: -90, position: "insideLeft" }} />
                <Tooltip 
                  formatter={(value: number) => `${value.toFixed(1)}%`}
                  labelFormatter={(month) => 
                    new Date(month + "-01").toLocaleDateString("de-DE", { month: "long", year: "numeric" })
                  }
                />
                <Legend />
                {cumulativeData.slice(0, 10).map((empData, index) => (
                  <Line 
                    key={empData.employeeName}
                    type="monotone" 
                    dataKey={empData.employeeName}
                    stroke={CHART_COLORS[index % CHART_COLORS.length]}
                    strokeWidth={2}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
            
            {/* Aktuelle kumulative Performance */}
            <div className="mt-6">
              <h4 className="text-sm font-semibold mb-3">Aktuelle kumulative Performance</h4>
              <div className="space-y-2">
                {cumulativeData.slice(0, 10).map((empData, index) => (
                  <div key={empData.employeeName} className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
                    <span className="text-sm min-w-[30px] flex-shrink-0">
                      {index + 1}. 
                    </span>
                    <span className="text-sm flex-1 min-w-0 truncate">{empData.employeeName}</span>
                    <div className="flex items-center gap-2 sm:gap-3 w-full sm:flex-1">
                      <Progress value={empData.currentPercentage} className="flex-1" />
                      <span className="text-sm font-medium w-12 sm:w-16 text-right flex-shrink-0">
                        {empData.currentPercentage.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KI-Auswertung Mitarbeitergespräche */}
      {!isEmployee && <ConversationAISummary />}
    </div>
  );
};

export default Dashboard;
